// client/src/hooks/useReliableCalls.js
import { useState, useEffect, useCallback, useRef } from 'react';
import { reliableCallManager } from '../utils/ReliableCallManager';
import { reliableSessionService } from '../services/reliableSessionService';

const CUSTOMER_DATA_KEY = 'bzct_customer_data_v1';
const LOADING_STAGE_KEY = 'bzct_loading_stage_v1';

// Helper to normalize phone to 10 digits (outside component)
const normalizePhone = (phone) => {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    return digits.length >= 10 ? digits.slice(-10) : digits;
};

const loadMap = (key) => {
    try {
        if (typeof window === 'undefined' || !window.localStorage) return new Map();
        const raw = window.localStorage.getItem(key);
        if (!raw) return new Map();
        const parsed = JSON.parse(raw);
        return new Map(Array.isArray(parsed) ? parsed : []);
    } catch {
        return new Map();
    }
};

const saveMap = (key, map) => {
    try {
        if (typeof window === 'undefined' || !window.localStorage) return;
        window.localStorage.setItem(key, JSON.stringify(Array.from(map.entries())));
    } catch {
        /* noop */
    }
};

export const useReliableCalls = (lastMessage, currentUser, connectionStatus) => {
    const [reliableCalls, setReliableCalls] = useState([]);

    // Store customer data separately by phone number — hydrated from localStorage so
    // it survives a manual page refresh.
    const customerDataMapRef = useRef(loadMap(CUSTOMER_DATA_KEY));

    // Track loading stage per call — also persisted across refreshes.
    const loadingStageMapRef = useRef(loadMap(LOADING_STAGE_KEY));

    useEffect(() => {
        if (currentUser?.username) {
            reliableCallManager.setUserExtension(currentUser.username);
        }
    }, [currentUser]);

    const getCustomerDataForPhone = useCallback((phoneNumber) => {
        if (!phoneNumber) return null;
        const normalized = normalizePhone(phoneNumber);
        return customerDataMapRef.current.get(normalized) || null;
    }, []);

    const getLoadingStageForCall = useCallback((callId) => {
        return loadingStageMapRef.current.get(callId) || 0;
    }, []);

    const updateCalls = useCallback(() => {
        const active = reliableCallManager.getActiveCalls();

        // Enrich calls with customer data AND loading stage
        const enrichedCalls = active.map(call => {
            const phoneNormalized = normalizePhone(call.phoneNumber);

            // Live per-phone map wins; fall back to the customerData the backend
            // persisted onto the call record (restored via the Redis snapshot) so
            // the card's match badges / customer id / RECORDS survive a
            // localStorage clear or a fresh device — not just the name.
            const customerData =
                customerDataMapRef.current.get(phoneNormalized) || call.customerData || null;

            // Get loading stage for this call
            const loadingStage = loadingStageMapRef.current.get(call.callId) || 0;

            if (customerData) {
                // Priority: Tekion → CRM (Vinsolutions) → Twilio
                let callerName = call.callerName || call.phoneNumber;

                const hasCRMData = !!(customerData.contact && customerData.contact.contactId);
                const hasTekionData = !!(customerData.tekionData && customerData.tekionData.customerId);

                if (hasTekionData && customerData.tekionData) {
                    const td = customerData.tekionData;
                    callerName = td.displayName ||
                        `${td.firstName || ''} ${td.lastName || ''}`.trim() ||
                        callerName;
                } else if (hasCRMData && customerData.contact?.fullName) {
                    callerName = customerData.contact.fullName;
                } else if (customerData.hasTwilioData && customerData.twilioData?.callerName) {
                    callerName = customerData.twilioData.callerName;
                }

                return {
                    ...call,
                    callerName: callerName,
                    customerData: customerData,
                    loadingStage: loadingStage
                };
            }
            return {
                ...call,
                loadingStage: loadingStage
            };
        });

        setReliableCalls([...enrichedCalls]);

        // Persist enrichment maps so a page refresh can restore the same view.
        saveMap(CUSTOMER_DATA_KEY, customerDataMapRef.current);
        saveMap(LOADING_STAGE_KEY, loadingStageMapRef.current);
    }, []);

    // Pull the authoritative snapshot from the backend (Redis) and reconcile it
    // into the local manager. The backend's 3CX socket stays connected even when
    // this browser's WS is down, so its snapshot carries the true Ended state for
    // calls that ended while we were logged out — which heals a card stuck on
    // Ringing/Connected. Non-fatal: on any failure we keep the local view.
    // Retries on failure: this is the ONLY thing that heals a card stuck on
    // Connected after a mid-call WS drop, and no further socket message will
    // arrive to trigger another attempt once the call has already ended. A
    // single silent failure used to leave the card wrong until a manual refresh.
    const hydrateFromServer = useCallback(async (attempt = 1) => {
        if (!currentUser?.username) return;
        const snapshot = await reliableSessionService.fetchSnapshot(currentUser.username);
        if (snapshot) {
            reliableCallManager.mergeSnapshot(snapshot);
            updateCalls();
            return;
        }
        if (attempt < 3) {
            setTimeout(() => hydrateFromServer(attempt + 1), 1000 * attempt);
        } else {
            console.warn('useReliableCalls: snapshot heal failed after 3 attempts — card may be stale');
        }
    }, [currentUser, updateCalls]);

    // On mount (and whenever the user identity becomes known), pull the call
    // list from the server snapshot. Call state is no longer persisted locally,
    // so this is the ONLY way cards come back after a refresh — deliberately,
    // since a stale local copy used to resurrect ended calls as false
    // Ringing/Connected. Covers the login + page-refresh scenarios.
    useEffect(() => {
        if (!currentUser?.username) return;
        updateCalls();
        hydrateFromServer();
    }, [currentUser, updateCalls, hydrateFromServer]);

    // Heal on every disconnected → connected transition of the socket itself.
    // The 'connected'/'authenticated' MESSAGE trigger below is not sufficient on
    // its own: it fires once, and if it is missed (message lands while
    // currentUser is briefly null during a token refresh, or the fetch fails)
    // nothing retries — the call has already ended, so no further socket message
    // will ever arrive. Driving off connection STATE instead means the heal is
    // tied to the same signal as the green status icon the user sees.
    // Idempotent: mergeSnapshot is a server-wins overwrite.
    const prevConnectionRef = useRef(connectionStatus);
    useEffect(() => {
        const prev = prevConnectionRef.current;
        prevConnectionRef.current = connectionStatus;
        if (connectionStatus === 'connected' && prev !== 'connected') {
            hydrateFromServer();
        }
    }, [connectionStatus, hydrateFromServer]);

    useEffect(() => {
        if (!lastMessage) return;

        const { type, data, callInfo } = lastMessage;

        // On a fresh WS (re)connection, re-pull the server snapshot so a card
        // that ended during a mid-session drop heals as soon as we reconnect.
        if (type === 'connected' || type === 'authenticated') {
            hydrateFromServer();
            return;
        }

        if (type === 'reliable_participant_data') {
            reliableCallManager.addOrUpdateParticipant(data);
            updateCalls();
        } else if (type === 'reliable_call_ended') {
            reliableCallManager.handleCallEnded(data);
            updateCalls();
        } else if (type === 'reliable_ringgroup_update') {
            reliableCallManager.handleRingGroupUpdate(data);
            updateCalls();
        }
        else if (type === 'twilio_data') {
            console.log('📞 useReliableCalls: Received twilio_data', lastMessage);
            const phoneNumber = callInfo?.phoneNumber || callInfo?.callerNumber;
            const callId = callInfo?.callId;

            if (phoneNumber && lastMessage.data) {
                const normalized = normalizePhone(phoneNumber);
                const existingData = customerDataMapRef.current.get(normalized) || {};

                const hasCRMData = !!(existingData.contact && existingData.contact.contactId);
                const hasTekionData = !!(existingData.tekionData && existingData.tekionData.customerId);

                if (hasCRMData || hasTekionData) {
                    console.log(`📞 useReliableCalls: CRM/Tekion exists, storing Twilio but not displaying for ${normalized}`);
                    customerDataMapRef.current.set(normalized, {
                        ...existingData,
                        twilioData: lastMessage.data,
                    });
                } else {
                    customerDataMapRef.current.set(normalized, {
                        ...existingData,
                        twilioData: lastMessage.data,
                        hasTwilioData: true
                    });

                    if (callId) {
                        const currentStage = loadingStageMapRef.current.get(callId) || 0;
                        if (currentStage < 2) {
                            loadingStageMapRef.current.set(callId, 2);
                        }
                    }

                    console.log('📞 useReliableCalls: Stored Twilio data for', normalized, 'hasTwilioData: true');
                }
                updateCalls();
            }
        }
        else if (type === 'progressive_update') {
            const callId = lastMessage.callInfo?.callId;
            const stage = lastMessage.stage;

            if (callId && stage) {
                const currentStage = loadingStageMapRef.current.get(callId) || 0;
                if (stage > currentStage) {
                    loadingStageMapRef.current.set(callId, stage);
                    console.log(`📊 useReliableCalls: Updated stage for ${callId} to ${stage}`);
                }
            }

            const phoneNumber = lastMessage.callInfo?.callerNumber || lastMessage.callInfo?.phoneNumber;
            if (phoneNumber && lastMessage.data) {
                const normalized = normalizePhone(phoneNumber);
                const existingData = customerDataMapRef.current.get(normalized) || {};

                // ✅ CRITICAL FIX: MERGE data - preserve existing Twilio data!
                const newData = {
                    ...existingData,
                    ...lastMessage.data,
                    // Explicitly preserve Twilio data from earlier lookup
                    twilioData: existingData.twilioData || lastMessage.data?.twilioData,
                };

                // Determine hasTwilioData based on whether CRM data exists
                const hasCRMData = !!(newData.contact && newData.contact.contactId);
                const hasTekionData = !!(newData.tekionData && newData.tekionData.customerId);

                // Only show Twilio if no CRM data AND we have Twilio data
                newData.hasTwilioData = !hasCRMData && !hasTekionData && !!(newData.twilioData?.callerName);

                customerDataMapRef.current.set(normalized, newData);
                console.log(`📊 useReliableCalls: Merged progressive_update for ${normalized}, hasTwilioData: ${newData.hasTwilioData}`);
            }

            updateCalls();
        }
        else if (type === 'complete_customer_data') {
            console.log('📊 useReliableCalls: Received complete_customer_data', lastMessage);
            const phoneNumber = callInfo?.phoneNumber || callInfo?.callerNumber;
            const callId = callInfo?.callId;

            if (phoneNumber) {
                const normalized = normalizePhone(phoneNumber);
                const existingData = customerDataMapRef.current.get(normalized) || {};

                const hasVinData = !!(lastMessage.vinSolutions?.contact && !lastMessage.vinSolutions?.noVinSolutionsData);
                const hasTekionData = !!(lastMessage.tekion && Object.keys(lastMessage.tekion).length > 0);

                // ✅ FIX: Preserve existing Twilio data
                const existingTwilioData = existingData.twilioData || lastMessage.twilioData;
                const shouldUseTwilio = !hasVinData && !hasTekionData && !!(existingTwilioData?.callerName);

                // Merge, don't replace — every other handler here spreads
                // existingData first, and this one used to be the exception.
                // The stage-3/4 progressive updates are what carry the LEAD data
                // (leads / leadCount / allContactsLeadSummary / allContactsData /
                // allLeadsData), and crmHasLeadMatch reads those to decide the
                // "✓ CRM Match" badge. complete_customer_data arrives last and
                // its vinSolutions payload does not always carry them — leaner
                // early-return paths omit them, and a ring group emits one
                // complete_customer_data per member. Replacing wholesale wiped
                // the leads, so the card kept the contact's NAME but dropped to
                // "No Match" until opening the details view re-fetched them.
                const newData = {
                    ...existingData,
                    ...(lastMessage.vinSolutions || {}),
                    tekionData: lastMessage.tekion,
                    twilioData: existingTwilioData || null,
                    hasTwilioData: shouldUseTwilio,
                    hasTekionData: hasTekionData,
                    noVinSolutionsData: !hasVinData
                };

                // Authoritative-resolution guard. leadCount / allContactsLeadSummary
                // arrive ONLY in the stage-3 progressive_update and are NOT in the
                // stage-4 completeData, so the ...existingData merge above keeps
                // whatever those aggregates were — including a STALE value from a
                // PRIOR call to this number left in localStorage (the reliable /
                // ring-group path often sends no fresh stage-3). crmHasLeadMatch
                // reads those aggregates, so a no-lead number could show "✓ CRM
                // Match" live while Redis (computed from the authoritative payload)
                // correctly shows "No Match". When this complete carries the
                // authoritative per-contact data (allContactsData present), let it
                // decide the match: drop the inherited aggregates so crmHasLeadMatch
                // uses allContactsData / leads. A truly lean complete (no
                // allContactsData) keeps them, preserving the earlier fix.
                if (Array.isArray(lastMessage.vinSolutions?.allContactsData)) {
                    delete newData.leadCount;
                    delete newData.allContactsLeadSummary;
                }

                customerDataMapRef.current.set(normalized, newData);

                if (callId) {
                    loadingStageMapRef.current.set(callId, 5);
                    console.log(`📊 useReliableCalls: Set stage 5 (complete) for ${callId}`);
                }

                console.log('📊 useReliableCalls: Stored complete data for', normalized, {
                    hasVinData,
                    hasTekion: hasTekionData,
                    hasTwilio: shouldUseTwilio,
                    twilioName: newData.twilioData?.callerName
                });
                updateCalls();
            }
        }
        else if (type === 'multi_call_update') {
            console.log('📊 useReliableCalls: Received multi_call_update', lastMessage.callInfo?.callId);
            const phoneNumber = lastMessage.callInfo?.callerNumber || lastMessage.callInfo?.phoneNumber;
            const callId = lastMessage.callInfo?.callId;
            const stage = lastMessage.stage;

            if (callId && stage) {
                const currentStage = loadingStageMapRef.current.get(callId) || 0;
                if (stage > currentStage) {
                    loadingStageMapRef.current.set(callId, stage);
                }
            }

            if (phoneNumber && lastMessage.data) {
                const normalized = normalizePhone(phoneNumber);
                const existingData = customerDataMapRef.current.get(normalized) || {};

                // ✅ CRITICAL FIX: MERGE data - preserve Twilio!
                const newData = {
                    ...existingData,
                    ...lastMessage.data,
                    twilioData: existingData.twilioData || lastMessage.data?.twilioData,
                };

                const hasCRMData = !!(newData.contact && newData.contact.contactId);
                const hasTekionData = !!(newData.tekionData && newData.tekionData.customerId);
                newData.hasTwilioData = !hasCRMData && !hasTekionData && !!(newData.twilioData?.callerName);

                customerDataMapRef.current.set(normalized, newData);
            }

            // Process other calls in the multi-call update
            if (lastMessage.multiCall?.otherCallsData) {
                lastMessage.multiCall.otherCallsData.forEach(otherCall => {
                    const otherPhone = otherCall.phoneNumber;
                    if (otherPhone && otherCall.data) {
                        const otherNormalized = normalizePhone(otherPhone);
                        const existingOtherData = customerDataMapRef.current.get(otherNormalized) || {};

                        const newOtherData = {
                            ...existingOtherData,
                            ...otherCall.data,
                            twilioData: existingOtherData.twilioData || otherCall.data?.twilioData,
                        };

                        const hasCRMData = !!(newOtherData.contact && newOtherData.contact.contactId);
                        const hasTekionData = !!(newOtherData.tekionData && newOtherData.tekionData.customerId);
                        newOtherData.hasTwilioData = !hasCRMData && !hasTekionData && !!(newOtherData.twilioData?.callerName);

                        customerDataMapRef.current.set(otherNormalized, newOtherData);
                    }

                    if (otherCall.callId && otherCall.stage) {
                        const currentStage = loadingStageMapRef.current.get(otherCall.callId) || 0;
                        if (otherCall.stage > currentStage) {
                            loadingStageMapRef.current.set(otherCall.callId, otherCall.stage);
                        }
                    }
                });
            }

            updateCalls();
        }
    }, [lastMessage, updateCalls, hydrateFromServer]);

    const removeCall = useCallback((callId) => {
        reliableCallManager.removeCall(callId);
        loadingStageMapRef.current.delete(callId);
        updateCalls();
    }, [updateCalls]);

    const clearAll = useCallback(() => {
        // Keep calls that are still in progress (Ringing/Connected) so clearing
        // mid-call doesn't wipe the live card from the UI. Only finished calls go.
        const liveCalls = reliableCallManager.getActiveCalls()
            .filter(c => c.status !== 'Ended');
        const liveCallIds = new Set(liveCalls.map(c => c.callId));
        const livePhones = new Set(liveCalls.map(c => normalizePhone(c.phoneNumber)));

        reliableCallManager.clearAll(liveCallIds);

        // Prune the enrichment maps, but keep entries tied to the live calls.
        for (const callId of Array.from(loadingStageMapRef.current.keys())) {
            if (!liveCallIds.has(callId)) {
                loadingStageMapRef.current.delete(callId);
            }
        }
        for (const phone of Array.from(customerDataMapRef.current.keys())) {
            if (!livePhones.has(phone)) {
                customerDataMapRef.current.delete(phone);
            }
        }
        // Persist the pruned maps (or remove the keys entirely if nothing's left).
        if (loadingStageMapRef.current.size > 0) {
            saveMap(LOADING_STAGE_KEY, loadingStageMapRef.current);
        }
        if (customerDataMapRef.current.size > 0) {
            saveMap(CUSTOMER_DATA_KEY, customerDataMapRef.current);
        }
        try {
            if (typeof window !== 'undefined' && window.localStorage) {
                if (customerDataMapRef.current.size === 0) {
                    window.localStorage.removeItem(CUSTOMER_DATA_KEY);
                }
                if (loadingStageMapRef.current.size === 0) {
                    window.localStorage.removeItem(LOADING_STAGE_KEY);
                }
            }
        } catch {
            /* noop */
        }
        // Also wipe the server-side (Redis) snapshot so cleared cards don't
        // reappear on the next login/refresh. Best-effort. The live call stays
        // in the manager's in-memory map and the backend re-mirrors it on its
        // next 3CX event.
        if (currentUser?.username) {
            reliableSessionService.clearSnapshot(currentUser.username);
        }
        updateCalls();
    }, [updateCalls, currentUser]);

    // Auto-remove ended/abandoned calls after 10 seconds
    useEffect(() => {
        const cleanupInterval = setInterval(() => {
            const active = reliableCallManager.getActiveCalls();
            const now = Date.now();
            let hasChanges = false;

            active.forEach(call => {
                const isEnded = call.callEnded || call.status === 'Ended';
                const endTime = call.endedAt || (call.lastUpdate ? new Date(call.lastUpdate).getTime() : 0);

                if (isEnded && (now - endTime > 3600000)) { // 60 minutes = 3,600,000 ms
                    reliableCallManager.removeCall(call.callId);
                    loadingStageMapRef.current.delete(call.callId);
                    hasChanges = true;
                }
            });

            if (hasChanges) {
                updateCalls();
            }
        }, 1000);

        return () => clearInterval(cleanupInterval);
    }, [updateCalls]);

    return {
        reliableCalls,
        removeCall,
        clearAll,
        refreshReliableCalls: updateCalls,
        getCustomerDataForPhone,
        getLoadingStageForCall
    };
};